import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, CalendarCheck, CalendarOff, Tag, Settings2, BellRing, Users, Package, Search, Star } from "lucide-react";
import { api, IMAGES } from "../lib/api";
import { EASE } from "../components/motion";
import { Requests } from "../components/admin/Requests";
import { Reminders } from "../components/admin/Reminders";
import { Planning } from "../components/admin/Planning";
import { ClientsAdmin } from "../components/admin/ClientsAdmin";
import { ReviewsAdmin } from "../components/admin/ReviewsAdmin";
import { StockAdmin } from "../components/admin/StockAdmin";
import { ServicesAdmin } from "../components/admin/ServicesAdmin";
import { SettingsAdmin } from "../components/admin/SettingsAdmin";

const TABS = [
  { id: "requests", label: "Rendez-vous", icon: CalendarCheck },
  { id: "reminders", label: "Rappels", icon: BellRing },
  { id: "planning", label: "Planning", icon: CalendarOff },
  { id: "clients", label: "Clientes", icon: Users },
  { id: "reviews", label: "Avis", icon: Star },
  { id: "stock", label: "Stock", icon: Package },
  { id: "services", label: "Prestations & Tarifs", icon: Tag },
  { id: "settings", label: "Paramètres", icon: Settings2 },
];

export default function Admin() {
  const [tab, setTab] = useState("requests");
  const [user, setUser] = useState(null);
  const [lowStock, setLowStock] = useState(0);
  const [clients, setClients] = useState([]);
  const [clientQuery, setClientQuery] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [showDrop, setShowDrop] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("nadou_token")) {
      navigate("/admin/login");
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        api.get("/admin/stock").then((s) => setLowStock(s.data.low_count)).catch(() => {});
        api.get("/admin/clients").then((c) => setClients(c.data.clients)).catch(() => {});
      })
      .catch(() => {
        localStorage.removeItem("nadou_token");
        navigate("/admin/login");
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("nadou_token");
    navigate("/admin/login");
  };

  if (!user) return <div className="min-h-screen bg-cream" />;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-white/70 backdrop-blur-xl border-b border-taupe/20 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-18 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={IMAGES.logo} alt="Nadou Skin" className="h-8 w-auto mix-blend-multiply" />
            <span className="text-[11px] tracking-[0.2em] uppercase text-taupe-dark">Espace pro</span>
          </div>
          <div className="flex items-center gap-5">
            <div className="relative hidden md:block">
              <Search className="w-3.5 h-3.5 absolute left-3.5 top-1/2 -translate-y-1/2 text-espresso/40" strokeWidth={1.5} />
              <input
                data-testid="admin-client-search"
                value={searchQ}
                onChange={(e) => { setSearchQ(e.target.value); setShowDrop(true); }}
                onFocus={() => setShowDrop(true)}
                onBlur={() => setTimeout(() => setShowDrop(false), 200)}
                placeholder="Rechercher une cliente…"
                className="bg-white border border-taupe/30 rounded-full pl-9 pr-4 py-2 text-sm text-espresso placeholder:text-espresso/40 focus:outline-none focus:ring-1 focus:ring-taupe transition-shadow duration-300 w-56"
              />
              {showDrop && searchQ.trim().length > 0 && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-taupe/25 rounded-2xl shadow-[0_16px_40px_rgba(44,36,27,0.14)] overflow-hidden z-50" data-testid="admin-search-dropdown">
                  {clients
                    .filter((c) => c.name.toLowerCase().includes(searchQ.trim().toLowerCase()) || c.phone.replace(/\D/g, "").includes(searchQ.replace(/\D/g, "")))
                    .slice(0, 5)
                    .map((c, i) => (
                      <button
                        key={i}
                        data-testid={`admin-search-result-${i}`}
                        onMouseDown={() => { setClientQuery(c.name); setTab("clients"); setSearchQ(""); setShowDrop(false); }}
                        className="w-full text-left px-5 py-3 hover:bg-cream transition-colors duration-200 border-b border-taupe/10 last:border-0"
                      >
                        <p className="text-sm text-espresso font-medium flex items-center gap-2">
                          {c.name}
                          {c.regular && <span className="text-[9px] tracking-[0.08em] uppercase text-taupe-dark bg-gold/15 rounded-full px-2 py-0.5">Régulière</span>}
                        </p>
                        <p className="text-xs text-espresso/50 mt-0.5">
                          {c.phone} · {c.visits} visite{c.visits > 1 ? "s" : ""}{c.next ? ` · prochain RDV ${c.next}` : ""}
                        </p>
                      </button>
                    ))}
                  {clients.filter((c) => c.name.toLowerCase().includes(searchQ.trim().toLowerCase())).length === 0 && (
                    <p className="px-5 py-4 text-sm text-espresso/45" data-testid="admin-search-empty">Aucune cliente trouvée.</p>
                  )}
                </div>
              )}
            </div>
            <span className="text-sm text-espresso/60 hidden sm:block" data-testid="admin-user-email">{user.email}</span>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="text-espresso/50 hover:text-espresso transition-colors duration-300 inline-flex items-center gap-2 text-xs tracking-[0.1em] uppercase"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Déconnexion
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
          <h1 className="font-display text-3xl lg:text-4xl text-espresso">Bonjour {user.name}</h1>
          <div className="mt-8 flex flex-wrap gap-2 border-b border-taupe/20 pb-4">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`rounded-full px-5 py-2.5 text-xs tracking-[0.1em] uppercase inline-flex items-center gap-2 transition-colors duration-300 ${
                  tab === t.id ? "bg-espresso text-cream" : "text-espresso/55 hover:text-espresso hover:bg-beige/60"
                }`}
              >
                <t.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                {t.label}
                {t.id === "stock" && lowStock > 0 && (
                  <span className="w-4 h-4 rounded-full bg-[#9E4747] text-white text-[9px] flex items-center justify-center" data-testid="stock-tab-badge">
                    {lowStock}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="mt-8">
            {tab === "requests" && <Requests />}
            {tab === "reminders" && <Reminders />}
            {tab === "planning" && <Planning />}
            {tab === "clients" && <ClientsAdmin q={clientQuery} setQ={setClientQuery} />}
            {tab === "reviews" && <ReviewsAdmin />}
            {tab === "stock" && <StockAdmin />}
            {tab === "services" && <ServicesAdmin />}
            {tab === "settings" && <SettingsAdmin />}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
